/**
 * IntakeSummarySheet — the ONE complete, printable summary of an intake.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE OWNER'S ASK (2026-06-22):
 *    "We need one printable sheet showing everything in one, two, or three
 *     pages … along with a Save & Submit button so that after filling, they
 *     know in complete reference what exactly they have filled, what all things
 *     are missing, and what they are submitting."
 *
 *  Unlike the per-module read-only render (which only shows the modules a tier
 *  selected, so it can MISS contact channels, uploaded plans/media and the
 *  per-person astrology details), this sheet reads the WHOLE `IntakeForm` and
 *  renders EVERYTHING captured — independent of module selection — so it is
 *  complete by construction. It also shows:
 *    • email + WhatsApp (contact channels)
 *    • every uploaded photo/video (thumbnails) and every floor PLAN (name/icon)
 *    • full astrology details per person (DOB · time · place · coordinates)
 *    • a "Still needed" list of mandatory-but-empty items
 *    • the stage-completion count and what is being submitted
 *
 *  Print: `@media print` hides the app chrome (visibility trick) and lays the
 *  sheet out to A4, so "Print / Save as PDF" yields a clean 1–3 page document.
 *  Pure presentational — all data + the `missing[]` list are passed in.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { IntakeForm, FloorInput, PersonInput, PropertyInput } from '../lib/intake';
import { PROJECT_TYPE_LABELS } from '../lib/propertyModel';
import { isPdfPlan } from './IntakeModule';

function useLabel(v: string | undefined): string {
  if (!v) return '';
  return (PROJECT_TYPE_LABELS as Record<string, string>)[v] || v;
}

function langLabel(l: string): string {
  return l === 'hi' ? 'Hindi' : 'English';
}

/* ── tiny presentational atoms (self-contained; no app-ui coupling) ── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="iss-section">
      <h3 className="iss-h3">{title}</h3>
      {children}
    </section>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="iss-row">
      <span className="iss-k">{k}</span>
      <span className="iss-v">{v}</span>
    </div>
  );
}

function FloorLine({ f }: { f: FloorInput }) {
  const bits = [f.floor || 'Floor', f.areaApprox ? `${f.areaApprox} sq ft` : ''].filter(Boolean);
  return (
    <div className="iss-floor">
      <span className="iss-v">{bits.join(' · ')}</span>
      {f.planUrl ? (
        <span className="iss-plan">
          {isPdfPlan(f) ? (
            <span className="iss-plan-icon" aria-label="PDF plan">📄</span>
          ) : (
            <img className="iss-plan-thumb" src={f.planUrl} alt={f.planName || 'Floor plan'} />
          )}
          <span className="iss-plan-name">{f.planName || 'Plan attached'}</span>
        </span>
      ) : (
        <span className="iss-missing-inline">No plan uploaded</span>
      )}
    </div>
  );
}

function PropertyBlock({ p, idx }: { p: PropertyInput; idx: number }) {
  const loc =
    p.coordPair || p.google_maps_url
      ? [p.coordPair, p.northRotation ? `North ${p.northRotation}°` : ''].filter(Boolean).join(' · ')
      : '';
  const areas = [
    p.builtUpAreaSqft ? `Built-up ${p.builtUpAreaSqft} sq ft` : '',
    p.plotAreaSqft ? `Plot ${p.plotAreaSqft} sq ft` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const floors = (p.floors || []).filter(
    (f) => (f.floor || '').trim() || (f.areaApprox || '').trim() || f.planUrl,
  );
  const media = (p.media || []).filter((m) => (m.url || '').trim());
  return (
    <div className="iss-prop">
      <div className="iss-prop-h">Property {idx + 1}{p.label ? ` — ${p.label}` : ''}</div>
      <Row k="Use" v={useLabel(p.use)} />
      <Row k="Location" v={loc} />
      {p.google_maps_url && <Row k="Maps link" v={<span className="iss-link">{p.google_maps_url}</span>} />}
      <Row k="Area" v={areas} />
      {floors.length > 0 && (
        <div className="iss-sub">
          <div className="iss-k">Floors &amp; plans</div>
          {floors.map((f) => (
            <FloorLine key={f.id} f={f} />
          ))}
        </div>
      )}
      <div className="iss-sub">
        <div className="iss-k">Photos &amp; videos {media.length > 0 ? `(${media.length})` : ''}</div>
        {media.length === 0 ? (
          <span className="iss-missing-inline">None uploaded</span>
        ) : (
          <div className="iss-media-grid">
            {media.map((m) => (
              <div key={m.id} className="iss-media">
                {m.thumbUrl || m.url ? (
                  <img src={m.thumbUrl || m.url} alt="" className="iss-media-thumb" />
                ) : (
                  <span className="iss-media-thumb iss-media-ph">{m.type === 'video' ? '🎬' : '🖼'}</span>
                )}
                <span className="iss-media-tag">{[m.floor, m.place].filter(Boolean).join(' · ') || m.type}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PersonBlock({ p, astroScope }: { p: PersonInput; astroScope: boolean }) {
  const birth = [
    (p.dob || '').trim() ? `DOB ${p.dob}` : '',
    (p.time_of_birth || '').trim() ? `Time ${p.time_of_birth}` : '',
    (p.place_of_birth || '').trim() ? `Place ${p.place_of_birth}` : '',
    (p.lat || '').trim() && (p.lon || '').trim() ? `(${p.lat}, ${p.lon})` : '',
    (p.tz || '').trim() ? p.tz : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const astroOn = p.astro_in_scope ?? astroScope;
  return (
    <div className="iss-person">
      <div className="iss-person-h">
        {p.name || '—'}
        {p.role ? ` · ${p.role}` : ''}
        {p.is_breadwinner ? ' · bread-earner' : ''}
      </div>
      {astroOn ? (
        birth ? (
          <div className="iss-v">🎂 {birth}</div>
        ) : (
          <div className="iss-missing-inline">Astrology on, but birth details missing</div>
        )
      ) : (
        <div className="iss-v iss-mute">Astrology not included</div>
      )}
    </div>
  );
}

export interface IntakeSummarySheetProps {
  serviceLabel: string;
  clientId: string;
  docTier: string;
  form: IntakeForm;
  /** Mandatory-but-empty items, human messages (already de-duped). */
  missing: string[];
  /** Stage completion, for the "what you're submitting" line. */
  stagesDone: number;
  stagesTotal: number;
  consentOnFile: boolean;
}

export default function IntakeSummarySheet({
  serviceLabel,
  clientId,
  docTier,
  form,
  missing,
  stagesDone,
  stagesTotal,
  consentOnFile,
}: IntakeSummarySheetProps) {
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const concerns = (form.presenting_concerns || []).filter((c) => (c.text || '').trim());
  const goals = (form.goals || []).filter((g) => (g || '').trim());
  const properties = (form.properties || []).filter(
    (p) => p.label || p.use || p.coordPair || p.google_maps_url || (p.floors || []).length || (p.media || []).length,
  );
  const persons = (form.persons || []).filter((p) => (p.name || '').trim() || p.dob);
  const complete = missing.length === 0 && stagesDone >= stagesTotal;

  return (
    <div className="iss-wrap" data-testid="intake-summary-sheet">
      <style>{ISS_CSS}</style>

      <div className="iss-head">
        <div>
          <div className="iss-title">{serviceLabel}</div>
          <div className="iss-sub-title">Intake summary</div>
        </div>
        <div className="iss-meta">
          <div><b>{clientId}</b></div>
          <div>{today}</div>
        </div>
      </div>

      <div className={`iss-status ${complete ? 'ok' : 'warn'}`}>
        {complete
          ? `Complete — all ${stagesTotal} stages filled. Ready to submit.`
          : `${stagesDone} of ${stagesTotal} stages complete · ${missing.length} item${missing.length === 1 ? '' : 's'} still needed.`}
      </div>

      {missing.length > 0 && (
        <Section title="Still needed before submitting">
          <ul className="iss-missing">
            {missing.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Contact & client">
        <Row k="Name" v={form.client_name} />
        <Row k="Company / firm" v={form.firm} />
        <Row k="Email" v={form.email || <span className="iss-missing-inline">Not provided</span>} />
        <Row k="WhatsApp" v={form.whatsapp || <span className="iss-missing-inline">Not provided</span>} />
        <Row k="Address" v={form.address} />
        <Row k="Property address" v={form.property_address} />
        <Row k="Country" v={form.country} />
        <Row k="Report language" v={langLabel(form.language)} />
      </Section>

      {(form.intended_use || goals.length > 0 || concerns.length > 0) && (
        <Section title="Goal & what's on your mind">
          <Row k="Intended use" v={useLabel(form.intended_use)} />
          <Row k="Goals" v={goals.join('; ')} />
          {concerns.length > 0 && (
            <div className="iss-sub">
              <div className="iss-k">Concerns</div>
              {concerns.map((c, i) => (
                <div key={i} className="iss-v">• {c.text}{c.severity ? ` (${c.severity})` : ''}</div>
              ))}
            </div>
          )}
        </Section>
      )}

      {properties.length > 0 && (
        <Section title={`Property details${properties.length > 1 ? ` (${properties.length})` : ''}`}>
          {properties.map((p, i) => (
            <PropertyBlock key={p.id} p={p} idx={i} />
          ))}
        </Section>
      )}

      {persons.length > 0 && (
        <Section title="People & astrology">
          {persons.map((p) => (
            <PersonBlock key={p.id} p={p} astroScope={form.astro_in_scope} />
          ))}
        </Section>
      )}

      <Section title="Consent">
        <Row
          k="Status"
          v={consentOnFile ? 'On file' : <span className="iss-missing-inline">Not yet recorded</span>}
        />
      </Section>

      <div className="iss-foot">
        <span>Writing to clients/{clientId}/intake/{docTier}</span>
        <span>Generated {today} · Aayatan Veda</span>
      </div>
    </div>
  );
}

/* ── styles — screen + print (A4, app-chrome hidden on print) ── */
const ISS_CSS = `
.iss-wrap { background:#fff; color:#1a1a1a; border:1px solid var(--rule,#e5e5e5); border-radius:8px; padding:22px 26px; font-size:12.5px; line-height:1.5; }
.iss-head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #1a1a1a; padding-bottom:8px; margin-bottom:12px; }
.iss-title { font-size:17px; font-weight:800; }
.iss-sub-title { font-size:11px; color:#666; text-transform:uppercase; letter-spacing:.5px; }
.iss-meta { text-align:right; font-size:11.5px; color:#444; }
.iss-status { font-weight:700; padding:7px 11px; border-radius:6px; margin-bottom:14px; font-size:12.5px; }
.iss-status.ok { background:#e8f5ee; color:#0f6f4e; }
.iss-status.warn { background:#fef3e2; color:#9a5b00; }
.iss-section { margin-bottom:14px; break-inside:avoid; }
.iss-h3 { font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.5px; color:#2f5d8a; margin:0 0 6px; border-bottom:1px solid #eee; padding-bottom:3px; }
.iss-row { display:flex; gap:10px; padding:2px 0; }
.iss-k { flex:0 0 132px; color:#777; font-weight:600; font-size:11.5px; }
.iss-v { flex:1; min-width:0; }
.iss-mute { color:#999; }
.iss-sub { margin:6px 0 2px; }
.iss-prop { border:1px solid #eee; border-radius:6px; padding:10px 12px; margin-bottom:8px; break-inside:avoid; }
.iss-prop-h { font-weight:800; font-size:12.5px; margin-bottom:4px; }
.iss-floor { display:flex; align-items:center; gap:8px; padding:2px 0; }
.iss-plan { display:inline-flex; align-items:center; gap:6px; }
.iss-plan-icon { font-size:18px; }
.iss-plan-thumb { width:28px; height:28px; object-fit:cover; border:1px solid #ddd; border-radius:4px; }
.iss-plan-name { font-size:11px; font-weight:600; color:#0f6f4e; max-width:160px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.iss-media-grid { display:flex; flex-wrap:wrap; gap:8px; margin-top:4px; }
.iss-media { width:64px; text-align:center; }
.iss-media-thumb { width:64px; height:48px; object-fit:cover; border:1px solid #ddd; border-radius:4px; display:flex; align-items:center; justify-content:center; font-size:22px; }
.iss-media-ph { background:#f3f3f3; }
.iss-media-tag { display:block; font-size:9px; color:#777; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.iss-person { padding:5px 0; border-bottom:1px solid #f2f2f2; break-inside:avoid; }
.iss-person-h { font-weight:700; }
.iss-missing, .iss-missing li { color:#9a5b00; }
.iss-missing { margin:2px 0 0; padding-left:18px; }
.iss-missing-inline { color:#b3261e; font-size:11.5px; font-style:italic; }
.iss-link { color:#2f5d8a; word-break:break-all; font-size:11px; }
.iss-foot { display:flex; justify-content:space-between; margin-top:14px; padding-top:8px; border-top:1px solid #eee; font-size:10px; color:#999; }
@media print {
  body * { visibility:hidden !important; }
  .iss-wrap, .iss-wrap * { visibility:visible !important; }
  .iss-wrap { position:absolute; left:0; top:0; width:100%; border:none; border-radius:0; padding:0; font-size:11px; }
  .iss-no-print { display:none !important; }
  @page { size:A4; margin:14mm; }
}
`;
